import "../../../components/common/Tables/DataTable.css"
import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import RootState from "../../../redux/states/root.state";
import {getLogsFile} from "../../../redux/actions/logs.actions";
import {AppDispatch} from "../../../redux/store";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { DownloadLineIcon } from "../../../components/common/Icons/Icons";

type Order = 'asc' | 'desc';
const headers = [
  {id:'status', label:'Status'},
  {id:'spa', label:'Spa'},
  {id:'created_at', label:'Sent'},
  {id:'updated_at', label:'Last Updated'},
  {id:'log_file_path', label:'Log File', align:'center'},
];

// The request's lifecycle, as the API names it, in words and a tone
const STATUS: Record<string, {label: string; tone: string}> = {
  PUBLISHED: {label: 'Request sent', tone: 'info'},
  PUBLISH_FAILED: {label: 'Publish failed', tone: 'bad'},
  UPLOADED: {label: 'Uploaded', tone: 'good'},
};

const formatDate = (value: any) =>
  value ? <span className="dt-date">{new Date(value).toLocaleString()}</span> : <span className="dt-na">—</span>;

const LogsTable: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {data:logs, loading:isLoading, meta} = useSelector((state: RootState) => state.logs);
  const [searchParams, setSearchParams] = useSearchParams();

  // Sorted by the API (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');

  // The file link is fetched on demand; remember which row is waiting on it
  const [downloading, setDownloading] = useState<any>(null);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.last_page ?? meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  const downloadFile = async (log: { id: any; }) => {
    setDownloading(log.id);
    try {
      const data: any = await dispatch(getLogsFile({log_request_id: log.id}));
      const url = data?.payload?.data?.url;
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  }

  const cellClass = (header: typeof headers[number]) => header.align ? 'dt-center' : undefined;

  const renderCell = (id: string, log: any) => {
    switch (id) {
      case 'status': {
        const status = STATUS[log?.status];
        return log?.status
          ? <span className={`dt-pill ${status?.tone ?? 'neutral'}`}>{status?.label ?? log.status}</span>
          : <span className="dt-na">—</span>;
      }

      case 'spa':
        return log?.spa_id ? (
          <a
            href={`/spa/${log.spa_id}`}
            className="dt-guid"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View spa"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/spa/${log.spa_id}`);
            }}
          >
            {log.spa_id}
          </a>
        ) : <span className="dt-na">N/A</span>;

      case 'created_at':
        return formatDate(log?.created_at);

      case 'updated_at':
        return formatDate(log?.updated_at);

      case 'log_file_path':
        return log?.log_file_path ? (
          <Tooltip title="Download log file" arrow>
            <button
              type="button"
              className="dt-act"
              onClick={() => downloadFile(log)}
              disabled={downloading === log.id}
              aria-label="Download log file"
            >
              {downloading === log.id
                ? <span className="spinner lp-side-spinner" role="progressbar" aria-label="Preparing download"/>
                : <DownloadLineIcon/>}
            </button>
          </Tooltip>
        ) : <span className="dt-na">—</span>;

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading logs"/></div>
        ) : logs?.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {header.id !== 'log_file_path' ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {logs.map((log: any, i: number)=>(
                <tr key={log?.id ?? i}>
                  {headers.map((header)=>(
                    <td key={header.id} className={cellClass(header)}>
                      {renderCell(header.id, log)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Logs Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={logs?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Log' : 'Logs'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};

export default LogsTable;
